import some from 'lodash/some';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { guideIdsToPathnames as rootCategories } from '../../data/rootCategories.json';
import { NOW_PLAYING_GUIDE_ID } from '../constants/dialogs/nowPlaying';
import vanityPathsToGuideIds from './guideItem/vanityPathsToGuideIds';
import keyMirror from './keyMirror';

const contentType = 'metadata.properties.program.contentType';

// Matches strings where the first character is a letter, and everything after it is a number.
const validGuideIdRegex = /^[a-zA-Z]\d+$/;

export const behaviors = {
  browse: 'Browse',
  play: 'Play',
  profile: 'Profile',
  search: 'Search',
  subscribe: 'Subscribe',
  dismiss: 'Dismiss',
  signIn: 'SignIn',
  signUp: 'SignUp',
  link: 'Link',
  select: 'Select',
};

// use for guide item API responses
export const types = {
  album: 'Album',
  artist: 'Artist',
  audioClip: 'AudioClip',
  category: 'Category',
  container: 'Container',
  link: 'link',
  program: 'Program',
  prompt: 'Prompt',
  station: 'Station',
  topic: 'Topic',
  user: 'User',
  region: 'Region',
};

// maps guideId prefixes to API guide item types
export const guideIdPrefixTypeMap = {
  a: types.category,
  c: types.category,
  d: types.album,
  g: types.category,
  i: types.audioClip,
  m: types.artist,
  p: types.program,
  r: types.region,
  s: types.station,
  t: types.topic,
  u: types.user,
  v: types.category,
};

// use for program guide item API responses
export const programTypes = {
  audiobook: 'Audiobook',
  podcast: 'Podcast',
  game: 'Game',
};

export const rootGenres = keyMirror('audiobooks', 'podcasts');

export const gameStatus = {
  expired: 'Expired',
  future: 'Future',
  live: 'Live',
};

export function matchGuideItemNumber(guideId) {
  if (!guideId) {
    return false;
  }
  const matchNumber = /^\d+$/;
  return matchNumber.test(guideId.substring(1));
}

export function isType(guideId, ...guideItemTypes) {
  if (!isEmpty(guideId)) {
    const isNumber = matchGuideItemNumber(guideId);
    const guideItemType = guideIdPrefixTypeMap[guideId[0]];
    return isNumber && guideItemTypes.includes(guideItemType);
  }
  return false;
}

export function getType(guideId) {
  return guideId ? guideIdPrefixTypeMap[guideId[0]] : null;
}

export function getTypeLowerCase(guideId) {
  const type = getType(guideId);
  return type ? type.toLowerCase() : null;
}

export function isTopic(guideId) {
  return isType(guideId, types.topic);
}

export function isAudioClip(guideId) {
  return isType(guideId, types.audioClip);
}

export function isStation(guideIdOrSlug) {
  const guideId = vanityPathsToGuideIds[guideIdOrSlug] || guideIdOrSlug;
  return isType(guideId, types.station);
}

export function isRootPath(guideId) {
  return some(rootCategories, (value, key) => guideId === key);
}

export function isNowPlaying(guideId) {
  return guideId === NOW_PLAYING_GUIDE_ID;
}

export function isProfile(guideIdOrSlug) {
  const guideId = vanityPathsToGuideIds[guideIdOrSlug] || guideIdOrSlug;
  return isType(
    guideId,
    types.station,
    types.program,
    types.artist,
    types.album,
  );
}

export function isCategory(guideId) {
  return (
    isType(guideId, types.category, types.region) ||
    isRootPath(guideId) ||
    isNowPlaying(guideId)
  );
}

export function isUser(guideId) {
  return isType(guideId, types.user);
}

// this should be used to check if a BROWSE page is classified as podcast
export function isPodcast(rootGenre) {
  return rootGenre === rootGenres.podcasts;
}

// this should be used to check if a PROFILE is a podcast profile
export function isPodcastProfile(guideItem) {
  return (
    get(guideItem, contentType, '').toLowerCase() ===
    programTypes.podcast.toLowerCase()
  );
}

export function isRegion(guideId) {
  // This should match 'regions' & 'r\d+'. But not 'recents'
  return isType(guideId, types.region) || guideId === rootCategories.regions;
}

export function isLanguage(guideId) {
  return guideId === rootCategories.languages;
}

export function isRootCategory(identifier) {
  return !!rootCategories[identifier];
}

export function isGame(program = {}) {
  return get(program, 'contentType') === programTypes.game;
}

export function isExpiredGame(program = {}) {
  const isExpired = get(program, 'eventStatus') === gameStatus.expired;
  return isGame(program) && isExpired;
}

export function isRadioHome(guideId) {
  return guideId === rootCategories.home;
}

/**
 * isValidGuideId - Use this to determine if an guide id conforms to the expected format, which
 * should be a letter, followed by numbers (only numbers). Examples:
 * s123456
 * t10393423908
 *
 * @param  {string}   guideId the identifier to check, which should conform to the format detailed
 *                    above.
 * @return {boolean}
 */
export function isValidGuideId(guideId) {
  return validGuideIdRegex.test(guideId);
}
